'use client';

import React, { useEffect, useRef } from 'react';
import { Box, VStack, HStack } from '@terra/ui-styled-system';
import {
  LegacyDialog,
  Text,
  Input,
  Badge,
  Kbd,
  SignalStatusBadge,
} from '@terra/ui-design-system';
import {
  CommandIcon,
  GlobeIcon,
  HierarchyIcon,
  Search01Icon,
  ShieldEnergyIcon,
} from '@terra/ui-icons';
import {
  useGlobalSearch,
  type SearchResult,
  type SearchResultType,
  type EndpointSearchData,
} from '../../hooks/useGlobalSearch';
import type { GroupedSignal } from '@terra/shared-contracts-signals';
import type { HttpRequest } from '../../store/proxy-store';

type GlobalSearchDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onNavigateToEndpoint?: (endpoint: EndpointSearchData) => void;
  onNavigateToSignal?: (signal: GroupedSignal) => void;
  onNavigateToRequest?: (request: HttpRequest) => void;
  onOpenSettings?: () => void;
  onOpenHistory?: () => void;
  onOpenIntercept?: () => void;
  onOpenReplay?: () => void;
};
const TYPE_ICONS: Record<SearchResultType, React.ReactNode> = {
  endpoint: <GlobeIcon size={14} />,
  signal: <ShieldEnergyIcon size={14} />,
  request: <HierarchyIcon size={14} />,
  webpage: <GlobeIcon size={14} />,
  action: <CommandIcon size={14} />,
};
const TYPE_COLORS: Record<SearchResultType, string> = {
  endpoint: 'blue',
  signal: 'orange',
  request: 'green',
  webpage: 'purple',
  action: 'gray',
};

function SearchResultItem({
  result,
  isSelected,
  onClick,
}: {
  result: SearchResult;
  isSelected: boolean;
  onClick: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isSelected && ref.current) {
      ref.current.scrollIntoView({ block: 'nearest' });
    }
  }, [isSelected]);

  return (
    <Box
      ref={ref}
      px="3"
      py="2"
      borderRadius="md"
      cursor="pointer"
      bg={isSelected ? 'bg.muted' : 'transparent'}
      _hover={{ bg: 'bg.muted' }}
      onClick={onClick}
    >
      <HStack gap="3" alignItems="flex-start">
        <Box color={`${TYPE_COLORS[result.type]}.solid`} mt="1" flexShrink={0}>
          {TYPE_ICONS[result.type]}
        </Box>
        <VStack gap="0" alignItems="flex-start" flex="1" minWidth="0">
          <Text
            fontSize="sm"
            fontWeight="medium"
            color="fg.default"
            lineClamp={1}
          >
            {result.title}
          </Text>
          <Text fontSize="xs" color="fg.muted" lineClamp={1}>
            {result.subtitle}
          </Text>
        </VStack>
        {result.type === 'signal' && result.data?.status && (
          <SignalStatusBadge status={result.data.status} />
        )}
        {result.type === 'request' &&
          result.data &&
          typeof result.data.status === 'number' && (
            <Badge size="sm" variant="emphasis">
              {result.data.status || '...'}
            </Badge>
          )}
      </HStack>
    </Box>
  );
}

function SearchResultGroup({
  label,
  results,
  selectedIndex,
  startIndex,
  onSelect,
}: {
  label: string;
  results: SearchResult[];
  selectedIndex: number;
  startIndex: number;
  onSelect: (result: SearchResult) => void;
}) {
  return (
    <VStack gap="1" alignItems="stretch" w="full">
      <Text
        fontSize="xs"
        fontWeight="semibold"
        color="fg.muted"
        px="3"
        py="1"
        textTransform="uppercase"
        letterSpacing="wider"
      >
        {label}
      </Text>
      {results.map((result, index) => (
        <SearchResultItem
          key={result.id}
          result={result}
          isSelected={selectedIndex === startIndex + index}
          onClick={() => onSelect(result)}
        />
      ))}
    </VStack>
  );
}

function EmptyState({ query }: { query: string }) {
  if (!query) {
    return (
      <VStack gap="3" py="8" alignItems="center">
        <Search01Icon size={32} color="var(--colors-fg-muted)" />
        <VStack gap="1" alignItems="center">
          <Text fontSize="sm" color="fg.muted">
            Search for endpoints, signals, or requests
          </Text>
          <Text fontSize="xs" color="fg.subtle">
            Type to start searching...
          </Text>
        </VStack>
      </VStack>
    );
  }

  return (
    <VStack gap="3" py="8" alignItems="center">
      <Search01Icon size={32} color="var(--colors-fg-muted)" />
      <VStack gap="1" alignItems="center">
        <Text fontSize="sm" color="fg.muted">
          No results found for "{query}"
        </Text>
        <Text fontSize="xs" color="fg.subtle">
          Try a different search term
        </Text>
      </VStack>
    </VStack>
  );
}

export function GlobalSearchDialog({
  isOpen,
  onOpenChange,
  onNavigateToEndpoint,
  onNavigateToSignal,
  onNavigateToRequest,
  onOpenSettings,
  onOpenHistory,
  onOpenIntercept,
  onOpenReplay,
}: GlobalSearchDialogProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    query,
    setQuery,
    searchResults,
    selectedIndex,
    handleKeyDown,
    handleSelect,
    clearSearch,
    hasResults,
  } = useGlobalSearch({
    onNavigateToEndpoint: (endpoint) => {
      onNavigateToEndpoint?.(endpoint);
      onOpenChange(false);
      clearSearch();
    },
    onNavigateToSignal: (signal) => {
      onNavigateToSignal?.(signal);
      onOpenChange(false);
      clearSearch();
    },
    onNavigateToRequest: (request) => {
      onNavigateToRequest?.(request);
      onOpenChange(false);
      clearSearch();
    },
    onOpenSettings: () => {
      onOpenSettings?.();
      onOpenChange(false);
      clearSearch();
    },
    onOpenHistory: () => {
      onOpenHistory?.();
      onOpenChange(false);
      clearSearch();
    },
    onOpenIntercept: () => {
      onOpenIntercept?.();
      onOpenChange(false);
      clearSearch();
    },
    onOpenReplay: () => {
      onOpenReplay?.();
      onOpenChange(false);
      clearSearch();
    },
  });

  // Focus input when dialog opens
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure dialog is mounted
      const timeout = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);

      return () => clearTimeout(timeout);
    } else {
      clearSearch();
    }
  }, [isOpen, clearSearch]);
  const handleOpenChange = (details: { open: boolean }) => {
    onOpenChange(details.open);
    if (!details.open) {
      clearSearch();
    }
  };

  // Calculate start indices for groups
  let currentIndex = 0;
  const groupStartIndices: number[] = searchResults.map((group) => {
    const startIndex = currentIndex;

    currentIndex += group.results.length;

    return startIndex;
  });

  return (
    <LegacyDialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <LegacyDialog.Backdrop />
      <LegacyDialog.Positioner>
        <LegacyDialog.Content
          maxWidth="600px"
          width="full"
          maxHeight="500px"
          overflow="hidden"
          p="0"
        >
          {/* Search Input */}
          <Box
            px="4"
            py="3"
            borderBottomWidth="1px"
            borderColor="border.default"
          >
            <HStack gap="3">
              <Search01Icon size={18} color="var(--colors-fg-muted)" />
              <Input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search endpoints, signals, requests..."
                size="md"
                flex="1"
                border="none"
                bg="transparent"
                _focus={{ boxShadow: 'none', outline: 'none' }}
              />
              <HStack gap="1">
                <Kbd size="sm">⌘</Kbd>
                <Kbd size="sm">⇧</Kbd>
                <Kbd size="sm">F</Kbd>
              </HStack>
            </HStack>
          </Box>

          {/* Results */}
          <Box maxHeight="400px" overflowY="auto" px="2" py="2">
            {hasResults ? (
              <VStack gap="3" alignItems="stretch">
                {searchResults.map((group, groupIndex) => (
                  <SearchResultGroup
                    key={group.type}
                    label={group.label}
                    results={group.results}
                    selectedIndex={selectedIndex}
                    startIndex={groupStartIndices[groupIndex]}
                    onSelect={(result) => {
                      handleSelect(result);
                      onOpenChange(false);
                      clearSearch();
                    }}
                  />
                ))}
              </VStack>
            ) : (
              <EmptyState query={query} />
            )}
          </Box>

          {/* Footer */}
          <Box
            px="4"
            py="2"
            borderTopWidth="1px"
            borderColor="border.default"
            bg="bg.subtle"
          >
            <HStack gap="4" justify="flex-end">
              <HStack gap="1" alignItems="center">
                <Kbd size="sm">↑↓</Kbd>
                <Text fontSize="xs" color="fg.muted">
                  Navigate
                </Text>
              </HStack>
              <HStack gap="1" alignItems="center">
                <Kbd size="sm">↵</Kbd>
                <Text fontSize="xs" color="fg.muted">
                  Open
                </Text>
              </HStack>
              <HStack gap="1" alignItems="center">
                <Kbd size="sm">Esc</Kbd>
                <Text fontSize="xs" color="fg.muted">
                  Close
                </Text>
              </HStack>
            </HStack>
          </Box>
        </LegacyDialog.Content>
      </LegacyDialog.Positioner>
    </LegacyDialog.Root>
  );
}

export { type GlobalSearchDialogProps };
